import cloneDeep from 'clone-deep'
import { docStatus } from 'pages/LoanSubmission/constants'
import { Input2, Section, Toggle } from 'stories/components'

import { defaultConditionClearRules, IConditionClearRules, normalizeConditionClearRules } from './types'

interface IProps {
  value?: Partial<IConditionClearRules>
  onChange: (value: IConditionClearRules) => void
}

export const ConditionClearRules = ({ value, onChange }: IProps) => {
  const config = normalizeConditionClearRules({
    ...defaultConditionClearRules(),
    ...value,
    documentStatuses: {
      ...defaultConditionClearRules().documentStatuses,
      ...(value?.documentStatuses || {}),
    },
  })

  const updateChecklist = (patch: Partial<IConditionClearRules>) => {
    onChange({ ...config, ...patch })
  }

  const updateStatus = (status: string, patch: Partial<IConditionClearRules['documentStatuses'][string]>) => {
    const next = cloneDeep(config)
    next.documentStatuses[status] = { ...next.documentStatuses[status], ...patch }
    onChange(next)
  }

  return (
    <div className="col-span-full mt-4">
      <Section title="Condition Clear Rules" />
      <p className="text-sm text-gray-600 my-3">
        Control whether incomplete checklists or selected document statuses block clearing a condition, and customize the
        error shown for each.
      </p>

      <div className="border rounded-md p-4 mb-4 bg-white">
        <Toggle
          id="require-checklist-to-clear"
          title="Require checklist completed to clear"
          value={config.requireChecklistToClear}
          onChange={(requireChecklistToClear: boolean) => updateChecklist({ requireChecklistToClear })}
        />
        <div className="mt-3">
          <Input2
            title="Checklist required error message"
            value={config.checklistRequiredMessage}
            disabled={!config.requireChecklistToClear}
            onChange={(checklistRequiredMessage: string) => updateChecklist({ checklistRequiredMessage })}
          />
        </div>
      </div>

      <div className="border rounded-md p-4 bg-white overflow-x-auto">
        <p className="text-sm font-semibold mb-3">Document statuses that block clearing</p>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left border-b">
              <th className="py-2 pr-3 font-medium">Status</th>
              <th className="py-2 pr-3 font-medium whitespace-nowrap">Block clear</th>
              <th className="py-2 font-medium">Error message</th>
            </tr>
          </thead>
          <tbody>
            {docStatus.map((status) => {
              const rule = config.documentStatuses[status]
              return (
                <tr key={status} className="border-b last:border-b-0 align-top">
                  <td className="py-3 pr-3 whitespace-nowrap">{status}</td>
                  <td className="py-3 pr-3">
                    <Toggle
                      id={`block-clear-${status}`}
                      value={!!rule?.blockClear}
                      onChange={(blockClear: boolean) => updateStatus(status, { blockClear })}
                    />
                  </td>
                  <td className="py-3 min-w-[280px]">
                    <Input2
                      title="Error message"
                      value={rule?.message || ''}
                      disabled={!rule?.blockClear}
                      onChange={(message: string) => updateStatus(status, { message })}
                    />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export * from './logic'
export * from './types'
